import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as Dialog from '@radix-ui/react-dialog';
import { lightboxClose } from './lightbox';
import { dialogMotion, unfold } from './motion';

/** Opens when the page hash names it (a hero jump chip like "Fees" → #fees), so jump links land on an open row. */
export function useHashOpen(id: string): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => location.hash === `#${id}` && setOpen(true);
    check();
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element)?.closest?.(`a[href="#${id}"]`);
      if (a) setOpen(true);
    };
    window.addEventListener('hashchange', check);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('hashchange', check);
      document.removeEventListener('click', onClick);
    };
  }, [id]);
  return [open, setOpen];
}

const Chevron = ({ open }: { open: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d={open ? 'M4 10L8 6L12 10' : 'M4 6L8 10L12 6'} />
  </svg>
);

interface DetailsRowProps {
  id: string;
  title: ReactNode;
  summary?: ReactNode;
  first?: boolean;
  children: ReactNode;
}

/**
 * One row of the phone "Before you apply" list (Paper 04 mobile DetailsAccordion): title and a one-line summary,
 * a round chevron, and the section's content underneath once opened. Each details section renders its own row.
 */
export function DetailsRow({ id, title, summary, first, children }: DetailsRowProps) {
  const [open, setOpen] = useHashOpen(id);
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} className={`border-b border-b-[--q-border]${first ? ' border-t border-t-[--q-ink]' : ''}`} id={id}>
      <Collapsible.Trigger className="flex w-full items-center justify-between gap-3 py-3 text-left text-inherit">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-heading text-[22px] leading-7 [&_p]:m-0">{title}</span>
          {summary && <span className="text-[13px] leading-[18px] text-muted-foreground">{summary}</span>}
        </span>
        <span
          className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[1.5px] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:2] ${
            open ? 'border-primary bg-primary text-primary-foreground' : 'border-[--q-ink] bg-[--q-paper] text-[--q-ink]'
          }`}
        >
          <Chevron open={open} />
        </span>
      </Collapsible.Trigger>
      <Collapsible.Content className={unfold}>
        <div className="flex flex-col gap-3 pb-[18px]">{children}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/** A virtual tour (Matterport and the like) in a full-screen frame, with a way out to the tour's own page. */
export function useTourModal() {
  // The last tour stays set after closing so the frame can fade out with its content.
  const [tour, setTour] = useState<{ url: string; title: string } | null>(null);
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const node = tour ? (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          className={`fixed inset-0 z-[85] flex flex-col bg-[--q-ink] text-[--q-on-image] outline-none ${dialogMotion.fade}`}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            closeRef.current?.focus();
          }}
        >
          <div className="flex h-[72px] shrink-0 items-center justify-between px-10 max-[767px]:px-[--gutter] max-[767px]:pt-3">
            <Dialog.Title className="text-[15px] font-medium leading-5 tracking-[.06em]">{tour.title}</Dialog.Title>
            <div className="flex items-center gap-5 max-[767px]:gap-3">
              <a className="text-[13px] leading-[18px] text-[--q-on-image] underline-offset-[3px] opacity-[.85]" href={tour.url} target="_blank" rel="noopener">Open in a new tab ↗</a>
              <Dialog.Close ref={closeRef} className={lightboxClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </Dialog.Close>
            </div>
          </div>
          <iframe className="w-full flex-auto bg-[--q-ink] [border:0]" src={tour.url} title={tour.title} allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer" allowFullScreen />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  ) : null;

  return {
    node,
    /** onClick for a tour link: opens the frame, and the plain link still works without JavaScript. */
    open: (url: string, title: string) => (e: { preventDefault: () => void }) => {
      e.preventDefault();
      setTour({ url, title });
      setOpen(true);
    },
  };
}
